import { Button, Form, Input, message, Select } from "antd";
import { useParams } from "react-router";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import modalStyles from "../../../../components/EComModal/index.module.scss";
import { EComModal } from "../../../../components/EComModal";
import ModalContent from "../../../../components/EComModal/ModalContent";
import ModalListItem from "../../../../components/EComModal/ModalListItem";
import { getTransactionAmountText, TransactionModel, TransactionSource } from "../../../../types/model/transactions";
import { refundTransaction } from "../../../../services/customer";
import { RootState } from "../../../../app/store";
import { customerSelectors } from "../../customerSlice";

export default function RefundTransactionModal({
  transaction,
  visible,
  onFinish,
  closeModal,
}: {
  transaction: TransactionModel;
  visible: boolean;
  onFinish: () => void;
  closeModal: () => void;
}) {
  const { customerId } = useParams<{ customerId: string }>();
  const customer = useSelector((state: RootState) => customerSelectors.selectById(state, customerId));
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);

  const refundReasonOptions = [
    "Duplicate subscription",
    "Renewal grace period",
    "Changed plan",
    "Lifetime moneyback guarantee",
    "Promo not applied",
    "Technical issues",
    "Irate customer",
    "Legal threat",
    "Other",
  ];

  useEffect(() => {
    form.validateFields(["refundAmount"]).then();
  }, []);

  const onSubmit = async () => {
    setIsLoading(true);
    try {
      const values = await form.validateFields();
      const payload = {
        auth0Id: customer?.auth0Id || "",
        transactionId: transaction.id,
        amount: Number(values.refundAmount),
        source: transaction.source,
        reason: values.refundReason,
        note: values.note?.trim(),
      };
      await refundTransaction(payload);
      message.success("Transaction Refunded Successfully.");
      onFinish();
      closeModal();
    } finally {
      setIsLoading(false);
    }
  };

  const isRecurly = transaction.source === TransactionSource.RECURLY;

  return (
    <EComModal
      visible={visible}
      title="Refund"
      okText="Refund"
      onCancel={closeModal}
      confirmLoading={isLoading}
      onOk={onSubmit}
    >
      <p>When refunding, the actual payment made will be prioritized for refund</p>
      <ModalContent>
        <ModalListItem label="Plan name:" value={transaction.productPlan.name}></ModalListItem>
        <ModalListItem label="Total payment :" value={getTransactionAmountText(transaction)}></ModalListItem>
        {transaction.price.refundableAmount &&
          isRecurly &&
          Number(transaction.price.refundableAmount) !== Number(transaction.price.total) && (
            <div>
              This transaction includes an upsell. Only the portion used in this transaction is refundable here. Any
              credit applied from the upsell should be refunded through the upsell transaction. The refundable amount
              for this transaction is {transaction.price.refundableAmount}
            </div>
          )}
      </ModalContent>
      <Form form={form} className={modalStyles.form}>
        <Form.Item
          label="Refund amount:"
          name="refundAmount"
          required
          rules={[
            { required: true, message: "Please input refund amount" },
            {
              pattern: /^(0|[1-9]\d*)(\.\d+)?$/,
              message: "Please input a valid number",
            },
            () => ({
              validator(_, value) {
                const { refundableAmount, total } = transaction.price;
                const isRecurly = transaction.source === TransactionSource.RECURLY;
                const isRecurlyFinalUpsellTransaction = isRecurly && Number(refundableAmount) === Number(total);

                const isOverTotalAmount =
                  (!isRecurly || isRecurlyFinalUpsellTransaction) && Number(value) > Number(transaction.price.total);

                if (isOverTotalAmount || (isRecurly && Number(value) > Number(refundableAmount))) {
                  return Promise.reject("Refund amount exceeds total payment");
                }

                return Promise.resolve();
              },
            }),
          ]}
          initialValue={transaction.price.total}
          className={modalStyles.formItem}
        >
          <Input
            type="number"
            addonBefore={transaction.currency.symbol}
            onBlur={async (e) => {
              await form.validateFields();
              form.setFieldsValue({
                refundAmount: Number(e.target.value).toFixed(2),
              });
            }}
            suffix={
              <Button
                type="link"
                style={{ padding: "0" }}
                size="small"
                onClick={() => {
                  form.setFieldsValue({
                    refundAmount: transaction.price.refundableAmount,
                  });
                }}
              >
                Full Amount
              </Button>
            }
          />
        </Form.Item>

        <Form.Item
          label="Refund reason:"
          name="refundReason"
          required
          rules={[{ required: true, message: "Please select refund reason." }]}
          className={modalStyles.formItem}
        >
          <Select placeholder="Please select refund reason here.">
            {refundReasonOptions.map((option) => (
              <Select.Option key={option} value={option}>
                {option}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Note:"
          name="note"
          required
          rules={[
            {
              validator: (_, value) => {
                if (!value || value.trim() === "") {
                  return Promise.reject(new Error("Please input refund reason"));
                }
                return Promise.resolve();
              },
            },
            { max: 50, message: "Note cannot exceed 50 characters" },
          ]}
          className={modalStyles.formItem}
        >
          <Input.TextArea placeholder="Please input note here." maxLength={50} showCount />
        </Form.Item>
      </Form>
    </EComModal>
  );
}
